import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Copy, Check, Loader2, AlertCircle, RefreshCw, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../hooks/useLanguage';
import { useAuth } from '../../hooks/useAuth';
import { evolutionAPI } from '../../utils/api';
interface StepPairingCodeProps {
  instanceName: string;
  phone?: string;
  onFinish: () => void;
  onBack: () => void;
}
const Confetti = () => {
  const emojis = ['🎉', '✨', '🎊', '⭐', '💫', '🌟', '🎈', '🎆'];
  const confettiPieces = Array.from({
    length: 30
  }, (_, i) => ({
    id: i,
    emoji: emojis[Math.floor(Math.random() * emojis.length)],
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2
  }));
  return <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-id="element-512">
      {confettiPieces.map(piece => <motion.div key={piece.id} initial={{
      y: -20,
      x: `${piece.x}vw`,
      opacity: 1,
      scale: 0
    }} animate={{
      y: '100vh',
      opacity: 0,
      scale: [0, 1.5, 1],
      rotate: [0, 360, 720]
    }} transition={{
      duration: piece.duration,
      delay: piece.delay,
      ease: 'easeOut'
    }} className="absolute text-3xl" data-id="element-513">
          {piece.emoji}
        </motion.div>)}
    </div>;
};
export function StepPairingCode({
  instanceName,
  phone,
  onFinish,
  onBack
}: StepPairingCodeProps) {
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [effectiveInstanceName, setEffectiveInstanceName] = useState(instanceName);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);
  const {
    language
  } = useLanguage();
  const {
    user
  } = useAuth();
  const isRTL = language === 'ar';
  useEffect(() => {
    if (instanceName) {
      setEffectiveInstanceName(instanceName);
      fetchPairingCode(instanceName);
    }
  }, [instanceName]);
  const fetchPairingCode = async (nameOverride?: string) => {
    const nameToUse = nameOverride || effectiveInstanceName || instanceName;
    try {
      setIsLoading(true);
      setError(null);
      console.log('Fetching pairing code for instance:', nameToUse);
      const response = await fetch('https://n8n.operines.me/webhook/evolution/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instanceName: nameToUse,
          businessName: nameToUse,
          email: user?.email || '',
          phone: phone || user?.phone || '',
          name: user?.name || '',
          action: 'create_pairing_code',
          connectionMethod: 'pairing_code',
          timestamp: new Date().toISOString()
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get pairing code');
      }
      const data = await response.json();
      console.log('Pairing code response:', data);
      if (data.success && data.pairingCode) {
        setPairingCode(data.pairingCode);
        startConnectionCheck(nameToUse);
      } else if (data.pairingCode) {
        setPairingCode(data.pairingCode);
        startConnectionCheck(nameToUse);
      } else {
        throw new Error('No pairing code received');
      }
    } catch (err: any) {
      console.error('Error fetching pairing code:', err);
      // Auto-retry with a new name
      const suffix = Math.random().toString(36).slice(2, 5);
      const newName = `${instanceName}${suffix}`;
      setEffectiveInstanceName(newName);
      setIsAutoRetrying(true);
      setError(isRTL ? 'هذا الاسم مستخدم من قبل، يجري تجربة اسم آخر تلقائياً...' : 'This name is already in use. Trying another name automatically...');
      setIsLoading(false);
      setTimeout(() => {
        setIsAutoRetrying(false);
        setError(null);
        fetchPairingCode(newName);
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };
  const checkConnectionStatus = async (nameToCheck?: string) => {
    try {
      const name = nameToCheck || effectiveInstanceName;
      const status = await evolutionAPI.checkConnection(name);
      const state = status?.state || status?.instance?.state || status?.connectionState;
      return state === 'open' || state === 'connected' || state === 'CONNECTED';
    } catch (err) {
      return false;
    }
  };
  const startConnectionCheck = (nameToCheck?: string) => {
    const name = nameToCheck || effectiveInstanceName;
    console.log('Starting connection check polling...');
    const interval = setInterval(async () => {
      try {
        const status = await evolutionAPI.checkConnection(name);
        const state = status?.state || status?.instance?.state || status?.connectionState;
        const connected = state === 'open' || state === 'connected' || state === 'CONNECTED';
        if (connected) {
          console.log('✅ WhatsApp connected!');
          setIsConnected(true);
          setShowConfetti(true);
          clearInterval(interval);
          // 🔔 Notify webhook - send original instanceName (brand name) not the suffixed one
          const payload = {
            instanceName: instanceName,
            effectiveInstanceName: name,
            businessName: instanceName,
            email: user?.email || '',
            phone: phone || user?.phone || '',
            name: user?.name || '',
            action: 'connected',
            connectionMethod: 'pairing_code',
            connectedAt: new Date().toISOString()
          };
          fetch('https://n8n.operines.me/webhook/evolution/events', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }).catch(() => fetch('https://n8n.operines.me/webhook/evolution/events', {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }).catch(() => {}));
          setTimeout(() => setShowConfetti(false), 4000);
          setTimeout(() => {
            console.log('🚀 Auto-finishing after pairing code connection...');
            onFinish();
          }, 2500);
        }
      } catch {
        // ignore
      }
    }, 2000);
    setTimeout(() => {
      console.log('Connection check timeout');
      clearInterval(interval);
    }, 300000);
  };
  const handleCopy = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode.replace(/-/g, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const handleRetry = () => {
    setPairingCode(null);
    setError(null);
    setIsAutoRetrying(false);
    const suffix = Math.random().toString(36).slice(2, 5);
    const newName = `${instanceName}${suffix}`;
    setEffectiveInstanceName(newName);
    fetchPairingCode(newName);
  };
  // Format pairing code with dashes for display
  const formatPairingCode = (code: string) => {
    // Remove any existing dashes and format as XXXX-XXXX
    const clean = code.replace(/-/g, '');
    if (clean.length === 8) {
      return `${clean.slice(0, 4)}-${clean.slice(4)}`;
    }
    return code;
  };
  return <>
      {showConfetti && <Confetti data-id="element-514" />}

      <div className="space-y-6" data-id="element-515">
        {/* Header */}
        <div className="text-center space-y-2" data-id="element-516">
          <motion.div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${error ? 'bg-red-100' : isConnected ? 'bg-green-100' : 'bg-emerald-100'}`} animate={isConnected ? {
          scale: [1, 1.2, 1],
          rotate: [0, 360]
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-517">
            {error ? <AlertCircle className="w-8 h-8 text-red-600" data-id="element-518" /> : isConnected ? <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-519" /> : <Smartphone className="w-8 h-8 text-emerald-600" data-id="element-520" />}
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-521">
            {isConnected ? <motion.div key="connected" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-522">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-523">
                  {isRTL ? '🎉 تم الربط بنجاح!' : '🎉 Successfully Connected!'}
                </h2>
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring'
            }} className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-200" data-id="element-524">
                  <Sparkles className="w-4 h-4 text-green-600" data-id="element-525" />
                  <span className="text-green-700 font-medium text-sm" data-id="element-526">
                    {isRTL ? 'متصل' : 'Connected'}
                  </span>
                </motion.div>
              </motion.div> : <motion.div key="pairing" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-527">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-528">
                  {error ? isRTL ? 'حدث خطأ' : 'Error Occurred' : isRTL ? 'كود الاقتران' : 'Pairing Code'}
                </h2>
                <p className="text-gray-500" data-id="element-529">
                  {error || (isRTL ? 'أدخل هذا الكود في واتساب للربط' : 'Enter this code in WhatsApp to connect')}
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Main Content */}
        <div className="mt-8" data-id="element-530">
          {isLoading ? <div className="flex flex-col items-center justify-center py-12" data-id="element-531">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-532">
                <Loader2 className="w-12 h-12 text-emerald-600 mb-4" data-id="element-533" />
              </motion.div>
              <p className="text-gray-500" data-id="element-534">
                {isRTL ? 'جاري إنشاء كود الاقتران...' : 'Generating Pairing Code...'}
              </p>
            </div> : error ? <div className="text-center py-8" data-id="element-535">
              <div className={`border rounded-lg p-4 mb-4 ${isAutoRetrying ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`} data-id="element-536">
                <div className="flex items-center justify-center gap-2" data-id="element-537">
                  {isAutoRetrying && <Loader2 className="w-4 h-4 text-yellow-600 animate-spin" data-id="element-538" />}
                  <p className={`text-sm font-medium ${isAutoRetrying ? 'text-yellow-800' : 'text-red-800'}`} data-id="element-539">
                    {error}
                  </p>
                </div>
              </div>
              {!isAutoRetrying && <Button onClick={handleRetry} variant="primary" fullWidth data-id="element-540">
                  <RefreshCw className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-541" />
                  {isRTL ? 'تجربة اسم آخر' : 'Try Another Name'}
                </Button>}
            </div> : pairingCode ? <div className="space-y-6" data-id="element-542">
              {/* Pairing Code Display */}
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} className="relative" data-id="element-543">
                {/* Animated Background */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-2xl blur-xl opacity-20" animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} data-id="element-544" />

                {/* Code Card */}
                <div className="relative bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-center shadow-lg max-w-sm mx-auto" data-id="element-545">
                  {/* Shine Effect */}
                  <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-xl" animate={{
                x: ['-200%', '200%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 2
              }} data-id="element-546" />

                  <p className="text-emerald-100 text-xs mb-2 font-medium" data-id="element-547">
                    {isRTL ? 'كود الاقتران الخاص بك' : 'Your Pairing Code'}
                  </p>

                  {/* The Code */}
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                delay: 0.2
              }} className="relative" data-id="element-548">
                    <div className="font-mono text-2xl sm:text-3xl font-bold text-white tracking-[0.15em] mb-3" data-id="element-549">
                      {formatPairingCode(pairingCode)}
                    </div>

                    {/* Animated Dots */}
                    <motion.div className="flex justify-center gap-1" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} data-id="element-550">
                      {[0, 1, 2].map(i => <motion.div key={i} className="w-1.5 h-1.5 bg-white/50 rounded-full" animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5]
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2
                  }} data-id="element-551" />)}
                    </motion.div>
                  </motion.div>

                  {/* Copy Button */}
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleCopy} className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${copied ? 'bg-white text-emerald-600' : 'bg-white/20 text-white hover:bg-white/30'}`} data-id="element-552">
                    {copied ? <>
                        <Check className="w-4 h-4" data-id="element-553" />
                        {isRTL ? 'تم النسخ!' : 'Copied!'}
                      </> : <>
                        <Copy className="w-4 h-4" data-id="element-554" />
                        {isRTL ? 'نسخ الكود' : 'Copy Code'}
                      </>}
                  </motion.button>
                </div>
              </motion.div>

              {/* Connection Status */}
              <AnimatePresence data-id="element-555">
                {isConnected && <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              y: -10
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.9,
              y: -10
            }} className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-556">
                    <div className="flex items-center justify-center gap-2" data-id="element-557">
                      <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-558" />
                      <p className="text-green-700 font-medium" data-id="element-559">
                        {isRTL ? '✅ تم الاتصال بنجاح!' : '✅ Connected Successfully!'}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Connection Steps */}
              {!isConnected && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-gray-50 border border-gray-200 rounded-xl p-5" data-id="element-560">
                  <p className="text-sm text-gray-700 text-center font-semibold mb-4" data-id="element-561">
                    {isRTL ? 'خطوات الربط:' : 'Connection Steps:'}
                  </p>
                  <ol className={`text-sm text-gray-600 space-y-3 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-562">
                    {[{
                ar: 'افتح واتساب على هاتفك',
                en: 'Open WhatsApp on your phone'
              }, {
                ar: 'اذهب إلى: الإعدادات ← الأجهزة المرتبطة',
                en: 'Go to: Settings → Linked Devices'
              }, {
                ar: 'اضغط على "ربط جهاز"',
                en: 'Tap "Link a Device"'
              }, {
                ar: 'اختر "الربط برقم الهاتف بدلاً من ذلك"',
                en: 'Select "Link with phone number instead"'
              }, {
                ar: 'أدخل الكود أعلاه واضغط متابعة',
                en: 'Enter the code above and tap continue'
              }].map((step, index) => <motion.li key={index} initial={{
                opacity: 0,
                x: isRTL ? 20 : -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.4 + index * 0.1
              }} className="flex items-start gap-3" data-id="element-563">
                        <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold" data-id="element-564">
                          {index + 1}
                        </span>
                        <span data-id="element-565">{isRTL ? step.ar : step.en}</span>
                      </motion.li>)}
                  </ol>
                </motion.div>}

              {/* Action Buttons */}
              <AnimatePresence data-id="element-566">
                {isConnected ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} transition={{
              delay: 0.2
            }} data-id="element-567">
                    <Button onClick={onFinish} variant="primary" fullWidth size="lg" className="shadow-lg bg-green-600 hover:bg-green-700" data-id="element-568">
                      {isRTL ? 'متابعة' : 'Continue'}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-569" />
                    </Button>
                  </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} data-id="element-570">
                    <Button onClick={onFinish} variant="primary" fullWidth size="lg" className="shadow-lg" data-id="element-571">
                      {isRTL ? 'تم الربط والمتابعة' : 'Connected & Continue'}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-572" />
                    </Button>
                  </motion.div>}
              </AnimatePresence>
            </div> : null}
        </div>

        {/* Back Button */}
        {!isConnected && !isLoading && <div className="pt-4" data-id="element-573">
            <Button type="button" variant="secondary" onClick={onBack} fullWidth data-id="element-574">
              {isRTL ? 'رجوع' : 'Back'}
            </Button>
          </div>}
      </div>
    </>;
}